import { TenantType } from '@xeetr/types/enums'
import { BookingCustomField } from '@xeetr/types/models/booking'
import { Settings } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { StatisticsConfig, useDashboard } from '@/hooks/useDashboard'
import { useTenant } from '@/hooks/useTenant'

const StatisticsConfigButton = () => {
  const { statisticsConfig, setStatisticsConfig } = useDashboard()
  const { tenant } = useTenant()
  const [open, setOpen] = useState(false)

  const handleConfigChange = (key: keyof StatisticsConfig, value: boolean) => {
    const newConfig = { ...statisticsConfig, [key]: value } as StatisticsConfig
    setStatisticsConfig(newConfig)
  }

  const handleCustomFieldChange = (
    fieldName: keyof StatisticsConfig['enabledCustomFields'],
    value: boolean
  ) => {
    const newEnabledCustomFields = { ...statisticsConfig.enabledCustomFields, [fieldName]: value }
    const newConfig = {
      ...statisticsConfig,
      enabledCustomFields: newEnabledCustomFields,
    } as StatisticsConfig
    setStatisticsConfig(newConfig)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="h-9 w-9">
          <Settings className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Statistiken anzeigen</h4>

          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="showTotalBookings"
                checked={statisticsConfig.showTotalBookings}
                onCheckedChange={(checked) =>
                  handleConfigChange('showTotalBookings', checked as boolean)
                }
              />
              <Label htmlFor="showTotalBookings" className="text-sm">
                Gesamtanzahl Buchungen
              </Label>
            </div>

            {tenant?.type === TenantType.FOOD && (
              <>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="showPeople"
                    checked={statisticsConfig.showPeople}
                    onCheckedChange={(checked) =>
                      handleConfigChange('showPeople', checked as boolean)
                    }
                  />
                  <Label htmlFor="showPeople" className="text-sm">
                    Anzahl Gäste
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="showTables"
                    checked={statisticsConfig.showTables}
                    onCheckedChange={(checked) =>
                      handleConfigChange('showTables', checked as boolean)
                    }
                  />
                  <Label htmlFor="showTables" className="text-sm">
                    Anzahl Tische
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="showTableAggregation"
                    checked={statisticsConfig.showTableAggregation}
                    onCheckedChange={(checked) =>
                      handleConfigChange('showTableAggregation', checked as boolean)
                    }
                  />
                  <Label htmlFor="showTableAggregation" className="text-sm">
                    Tischgrößen aufschlüsseln
                  </Label>
                </div>
              </>
            )}

            {tenant?.bookingCustomFields && tenant.bookingCustomFields.length > 0 && (
              <>
                <div className="space-y-1 border-t pt-2">
                  {tenant.bookingCustomFields.map((field: BookingCustomField) => (
                    <div key={field.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`customField-${field.id}`}
                        checked={statisticsConfig.enabledCustomFields?.[field.id] ?? true}
                        onCheckedChange={(checked) =>
                          handleCustomFieldChange(field.id, checked as boolean)
                        }
                      />
                      <Label htmlFor={`customField-${field.id}`} className="text-sm">
                        {field.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default StatisticsConfigButton
